<script lang="ts" setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { useFlowSteps } from '@Use/useFlowSteps';
import { getClientRequestDetailApi } from '@Api/requestFlow.api';
import { registerPaymentIntentApi, getUpdatedIntentApi } from '@Api/payment.api';
import { processPayment as processPaymentLogic } from '@Use/useMppPayment';
import { useMppPaymentForm } from '@Use/useMppPaymentForm';
import { useMppPolicyLabel } from '@Use/useMppPolicyLabel';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import { trackMppCancelled, trackMppPurchased } from '@Analytics/request.mixpanel';

import { FlowLayout, useCurrency, US_STATES } from '@Shared';

import MppCardField from './MppCardField.vue';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const requestDetailsStore = useRequestDetailsStore();
const { advanceFlow, goBackInFlow, resumeWireFlowStep } = useFlowNavigation();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { formatCurrency } = useCurrency();
const { formatPolicyLabel } = useMppPolicyLabel();
const { taskNavTitle } = useTaskNavTitle();
const instance = getCurrentInstance();
const proxy = instance?.proxy as { $apptoast: (msg: string, type: string) => void } | undefined;

const apis = {
    registerPaymentIntent: registerPaymentIntentApi,
    getUpdatedIntent: getUpdatedIntentApi,
};

// Form state (name, billingAddress, city, selectedState, zipCode, formData, validation)
const {
    name,
    billingAddress,
    city,
    selectedState,
    zipCode,
    formData,
    getFieldStatus,
    getFieldMessage,
    turnOnValidation,
    hasError,
    isValidationActive,
} = useMppPaymentForm();


// Stripe / card state
const stripeLoaded = ref(false);
const cardFieldRef = useTemplateRef('cardField');
const recipientEmail = ref<string | null>(null);
const cardErrorMessage = ref<string | null>(null);
const isCardComplete = ref(false);

const stripePublishableKey = (import.meta.env as Record<string, string | undefined>).VITE_STRIPE_PUBLISHABLE_KEY;
const stripeKey = stripePublishableKey ?? '';

// Lifecycle guard — prevents stale async callbacks from updating state after unmount
const isComponentMounted = ref(true);
onBeforeUnmount(() => { isComponentMounted.value = false; });

// Page state
const isProcessing = ref(false);

const formattedCost = computed(() => {
    const cost = requestSessionStore.selectedMppPolicy?.cost;
    return cost != null ? formatCurrency(cost, 2) : null;
});

const hasSelectedPolicy = computed(() => !!requestSessionStore.selectedMppPolicy);

const cardHasError = computed(() =>
    isValidationActive.value && (!isCardComplete.value || !!cardErrorMessage.value),
);

// Fetch request detail for recipient email (needed for payment)
onMounted(async () => {
    await initComponentSetup();
});

const onCardChange = ({ complete, errorMessage: msg }: { complete: boolean; errorMessage: string | null }) => {
    isCardComplete.value = complete;
    cardErrorMessage.value = msg;
};

// Actions
const confirmPayment = async () => {
    if (isProcessing.value) return;

    await turnOnValidation(true);
    if (hasError.value || cardHasError.value) return;

    const selectedPolicy = requestSessionStore.selectedMppPolicy;

    if (!selectedPolicy) {
        proxy?.$apptoast('Your payment session has expired or is incomplete. This can happen if you were inactive for too long or navigated here directly. Please use the link from your original email to restart the process.', 'error');
        return;
    }

    const cardField = cardFieldRef.value;
    if (!cardField) {
        proxy?.$apptoast('We couldn\'t submit your payment because the card entry field is still loading. Please wait a moment for it to finish loading, then click Confirm again.', 'error');
        return;
    }
    const { cardElement, stripeInstance } = cardField;

    if (!cardElement) {
        proxy?.$apptoast('We couldn\'t submit your payment because the card entry field is still loading. Please wait a moment for it to finish loading, then click Confirm again.', 'error');
        return;
    }

    if (!stripeInstance) {
        proxy?.$apptoast('We couldn\'t submit your payment because the payment system is no longer connected. This can happen due to a network interruption. Please refresh the page and re-enter your card details.', 'error');
        return;
    }

    if (!recipientEmail.value) {
        proxy?.$apptoast('We couldn\'t load the recipient\'s email address, which is required for payment. Please contact support for assistance.', 'error');
        return;
    }

    isProcessing.value = true;

    try {
        const result = await processPaymentLogic({
            form: formData.value,
            recipientEmail: recipientEmail.value,
            insurancePolicyId: selectedPolicy.internalPolicyId,
            isAboveMaxPolicy: selectedPolicy.isAboveMaxPolicy,
            stripe: stripeInstance,
            cardElement,
            apis,
        });

        if (!result.success) {
            proxy?.$apptoast(result.errorMessage ?? 'Something went wrong processing your payment. Please contact support for assistance.', 'error');
            return;
        }

        trackMppPurchased(formatPolicyLabel(selectedPolicy));
        await requestDetailsStore.fetchRequestDetails();

        if (!resumeWireFlowStep()) {
            advanceFlow();
        }
    } catch (err) {
        const error = err as {
            message?: string;
            response?: {
                status?: number;
                data?: unknown;
            };
            config?: {
                method?: string;
                url?: string;
            };
        };
        console.error('Payment processing failed:', {
            message: error.message,
            status: error.response?.status,
        });
        proxy?.$apptoast('We couldn\'t complete your payment because of a connection error with our payment system. Please check your internet connection and click Confirm again. If this keeps happening, contact support.', 'error');
    } finally {
        isProcessing.value = false;
    }
};

const cancelPayment = () => {
    if (isProcessing.value) return;
    trackMppCancelled(formatPolicyLabel(requestSessionStore.selectedMppPolicy));
    goBackInFlow();
};

const initComponentSetup = async () => {
    if (!requestSessionStore.selectedMppPolicy) {
        goBackInFlow();
        return;
    }

    if (!stripePublishableKey) {
        if (!isComponentMounted.value) return;
        proxy?.$apptoast('We couldn\'t load the payment form because the payment system is not configured. Please contact support for assistance.', 'error');
        return;
    }
    try {
        const stripeScriptResult = await loadStripe(stripePublishableKey);
        if (!isComponentMounted.value) return;
        if (!stripeScriptResult) {
            proxy?.$apptoast('We couldn\'t load the payment form. This may be caused by a network issue or a browser extension blocking the payment provider. Please refresh the page or try disabling ad blockers.', 'error');
            return;
        }
        stripeLoaded.value = true;
    } catch (err) {
        if (!isComponentMounted.value) return;
        console.error('Failed to initialize Stripe:', err);
        proxy?.$apptoast('We couldn\'t load the payment form. This may be caused by a network issue or a browser extension blocking the payment provider. Please refresh the page or try disabling ad blockers.', 'error');
        return;
    }
    
    try {
        const requestDetail = await getClientRequestDetailApi();
        if (!isComponentMounted.value) return;
        recipientEmail.value = requestDetail?.recipient?.email?.trim() || null;
        if (!recipientEmail.value) {
            proxy?.$apptoast('We couldn\'t load the recipient\'s email address, which is required for payment. Please contact support for assistance.', 'error');
            return;
        }
    } catch (err) {
        if (!isComponentMounted.value) return;
        console.error('Failed to initialize payment form:', err);
        proxy?.$apptoast('We couldn\'t load the payment form. This may be caused by a network issue or a browser extension blocking the payment provider. Please refresh the page or try disabling ad blockers.', 'error');
    }
};
</script>

<template>
    <flow-layout
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        is-white-label
        show-back-button
        @back-click="cancelPayment"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <app-form-content-layout>
            <h1 class="mpp-payment-details__headline" data-testid="mpp-headline">Complete payment</h1>

            <p class="mpp-payment-details__description" data-testid="mpp-description">
                Please enter your card details.
            </p>

            <div v-if="!stripeLoaded" class="mpp-payment-details__loading" data-testid="mpp-loading">
                Loading payment form...
            </div>

            <div v-if="stripeLoaded && hasSelectedPolicy" data-testid="mpp-form">
                <div class="mpp-payment-details__form-container">
                    <div class="mpp-payment-details__form">
                        <mpp-card-field
                            ref="cardField"
                            :stripe-key="stripeKey"
                            :card-has-error="cardHasError"
                            :card-error-message="cardErrorMessage"
                            @change="onCardChange"
                        />

                        <app-text-input
                            v-model="name"
                            label="Name"
                            :maxlength="50"
                            is-required
                            :field-status="getFieldStatus('name')"
                            :message="getFieldMessage('name')"
                            data-validation-field="name"
                            data-id="mpp-name-input"
                            autocomplete="cc-name"
                        />

                        <app-text-input
                            v-model="billingAddress"
                            label="Billing address"
                            :maxlength="256"
                            is-required
                            :field-status="getFieldStatus('billingAddress')"
                            :message="getFieldMessage('billingAddress')"
                            data-validation-field="billingAddress"
                            data-id="mpp-billing-input"
                            autocomplete="billing street-address"
                        />

                        <app-text-input
                            v-model="city"
                            label="City"
                            :maxlength="100"
                            is-required
                            :field-status="getFieldStatus('city')"
                            :message="getFieldMessage('city')"
                            data-validation-field="city"
                            data-id="mpp-city-input"
                            autocomplete="billing address-level2"
                        />

                        <app-column-group-layout>
                            <!-- autocomplete omitted: AppDropdown is a custom component with no native
                                 <select>/<input>, so browser autofill cannot interact with it. -->
                            <app-dropdown
                                v-model="selectedState"
                                :options="US_STATES"
                                id-field="abbreviation"
                                display-field="name"
                                label="State"
                                is-required
                                :field-status="getFieldStatus('state')"
                                :message="getFieldMessage('state')"
                                data-validation-field="state"
                                data-id="mpp-state-input"
                            />

                            <app-masked-input
                                v-model="zipCode"
                                label="Zip code"
                                :maxlength="5"
                                mask="#####"
                                is-required
                                input-mode="numeric"
                                :field-status="getFieldStatus('zipCode')"
                                :message="getFieldMessage('zipCode')"
                                data-validation-field="zipCode"
                                data-id="mpp-zip-input"
                                autocomplete="billing postal-code"
                            />
                        </app-column-group-layout>
                    </div>
                </div>

                <div class="mpp-payment-details__actions">
                    <app-button
                        expanded
                        size="large"
                        :is-loading="isProcessing"
                        :is-disabled="isProcessing || !stripeLoaded || !hasSelectedPolicy"
                        data-id="mpp-confirm-button"
                        @onClick="confirmPayment"
                    >
                        {{ formattedCost ? `Pay ${formattedCost}` : 'Confirm payment' }}
                    </app-button>

                    <app-button
                        expanded
                        size="large"
                        type="outline"
                        :is-disabled="isProcessing"
                        data-id="mpp-cancel-button"
                        @onClick="cancelPayment"
                    >
                        Cancel
                    </app-button>
                </div>
            </div>
        </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.mpp-payment-details {
    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
        margin: 0 0 1rem;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
    }

    &__form-container {
        background: $gray-lightest;
        border-radius: 0.375rem;
        padding: 1.5rem;
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
        margin: 0;
    }

    &__actions {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        margin-top: 2rem;
    }

    &__loading {
        text-align: center;
        padding: 2rem;
        color: $black-low;
        font-size: 1rem;
    }
}
</style>
